export type CertificateExpiryStatus =
  | 'EXPIRED_OR_DUE_SOON'
  | 'EXPIRING_WITHIN_3_MONTHS'
  | 'VALID'
  | 'UNKNOWN'

export type CertificateExpiryResult = {
  status: CertificateExpiryStatus
  label: string
  className: string
}

const startOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate())

const parseDateOnly = (value?: string | null) => {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  const year = Number(match[1])
  const monthIndex = Number(match[2]) - 1
  const day = Number(match[3])
  const parsed = new Date(year, monthIndex, day)
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== monthIndex ||
    parsed.getDate() !== day
  ) {
    return null
  }
  return startOfDay(parsed)
}

const addCalendarMonths = (value: Date, monthsToAdd: number) => {
  const year = value.getFullYear()
  const month = value.getMonth()
  const day = value.getDate()
  const targetMonth = month + monthsToAdd
  const firstOfTarget = new Date(year, targetMonth, 1)
  const lastDayOfTarget = new Date(firstOfTarget.getFullYear(), firstOfTarget.getMonth() + 1, 0).getDate()
  return startOfDay(
    new Date(firstOfTarget.getFullYear(), firstOfTarget.getMonth(), Math.min(day, lastDayOfTarget))
  )
}

export const getCertificateExpiryStatus = (
  expiryDate?: string | null,
  todayInput?: Date
): CertificateExpiryResult => {
  const expiry = parseDateOnly(expiryDate)
  if (!expiry) {
    return {
      status: 'UNKNOWN',
      label: 'No expiry date',
      className: 'secondary',
    }
  }

  const today = startOfDay(todayInput ? new Date(todayInput) : new Date())
  const oneMonth = addCalendarMonths(today, 1)
  const threeMonths = addCalendarMonths(today, 3)

  if (expiry.getTime() <= oneMonth.getTime()) {
    return {
      status: 'EXPIRED_OR_DUE_SOON',
      label: expiry.getTime() < today.getTime() ? 'Expired' : 'Expires within 1 month',
      className: 'danger',
    }
  }

  if (expiry.getTime() <= threeMonths.getTime()) {
    return {
      status: 'EXPIRING_WITHIN_3_MONTHS',
      label: 'Expires within 3 months',
      className: 'warning',
    }
  }

  return {
    status: 'VALID',
    label: 'Valid',
    className: 'success',
  }
}
